import { useParams, Navigate } from "react-router-dom";
import { useLang } from "@/App";
import { all, pick } from "@/data/images";
import { waLink, TEL1, EMAIL_RESERVAS } from "@/data/site";
import Hero from "@/components/Hero";
import { Reveal, BtnPrimario } from "@/components/Reveal";

export default function Espacio() {
  const { slug } = useParams();
  const { t, L } = useLang();
  const p = t.espacios[slug];
  if (!p) return <Navigate to={L("eventos")} replace />;
  const labels = t.espacios.labels;
  const g = t.global.footer;
  const cotizarLink = waLink(`${t.nav.reservar} — ${p.hero.titulo}: `);

  return (
    <div data-testid={`pagina-espacio-${slug}`}>
      <Hero pre={p.hero.pre} titulo={p.hero.titulo} fotos={[pick(p.hero.foto[0], 0) || all(p.hero.foto[0])[0]]} testid={`hero-espacio-${slug}`} />

      {/* 2. Presentación + Informes & Reservas */}
      <section className="py-20 md:py-28" data-testid={`esp-presentacion-${slug}`}>
        <div className="mx-auto grid max-w-[1200px] gap-12 px-6 md:px-10 lg:grid-cols-[7fr_4fr] lg:gap-20">
          <Reveal>
            <h2 className="text-[13px] font-bold tracking-[0.3em] text-terra">{p.hero.titulo.toUpperCase()}</h2>
            {p.texto.map((par, i) => (
              <p key={i} className="mt-6 max-w-2xl text-[15.5px] leading-relaxed text-ink-soft">{par}</p>
            ))}
          </Reveal>
          <Reveal delay={0.12} className="hidden lg:block">
            <div className="border-t-2 border-terra bg-sand-deep p-8 md:p-9" data-testid={`esp-informes-${slug}`}>
              <p className="text-[11px] font-bold tracking-[0.24em] text-terra">{g.informes}</p>
              <ul className="mt-6 space-y-4 text-sm">
                <li>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-mute">{g.telefono}</p>
                  <a href={`tel:${TEL1.replace(/\s/g, "")}`} className="mt-1 block font-semibold text-ink hover:text-terra" data-testid={`esp-telefono-${slug}`}>{TEL1}</a>
                </li>
                <li>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-mute">{g.correo}</p>
                  <a href={`mailto:${EMAIL_RESERVAS}`} className="mt-1 block break-all font-semibold text-ink hover:text-terra" data-testid={`esp-email-${slug}`}>{EMAIL_RESERVAS}</a>
                </li>
              </ul>
              <div className="mt-7">
                <BtnPrimario href={cotizarLink} testid={`esp-cotizar-btn-${slug}`}>{g.cotizar}</BtnPrimario>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 3. Dimensiones y capacidades */}
      <section className="bg-sand-deep py-20 md:py-28" data-testid={`esp-datos-${slug}`}>
        <div className="mx-auto max-w-[1100px] px-6 md:px-10">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal className="min-w-0">
              <h3 className="font-serif text-2xl font-light text-ink">{labels.dimensiones}</h3>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid={`esp-dimensiones-${slug}`}>
                <div className="border border-lineas bg-sand p-6 text-center">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-terra">{labels.metros}</p>
                  <p className="mt-3 font-serif text-2xl text-ink">{p.dimensiones[0]}</p>
                </div>
                <div className="border border-lineas bg-sand p-6 text-center">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-terra">{labels.metros2}</p>
                  <p className="mt-3 font-serif text-2xl text-ink">{p.dimensiones[1]}</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="min-w-0">
              <h3 className="font-serif text-2xl font-light text-ink">{labels.capacidades}</h3>
              <div className="mt-6 min-w-0" data-testid={`esp-capacidades-${slug}`}>
                {/* Móvil: transpuesta a 2 columnas (etiqueta | valor), 5 filas */}
                <div className="border border-lineas bg-sand lg:hidden">
                  {[labels.banquet, labels.coctel, labels.escuela, labels.auditorio, labels.u].map((l, i) => (
                    <div key={l} className="flex items-center justify-between gap-4 border-b border-lineas px-4 py-3 last:border-b-0">
                      <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-terra">{l}</span>
                      <span className="font-serif text-base text-ink">{p.capacidades[i]}</span>
                    </div>
                  ))}
                </div>
                {/* Desktop: tabla horizontal */}
                <div className="hidden overflow-x-auto lg:block">
                  <table className="ficha bg-sand">
                    <thead>
                      <tr className="border-b border-lineas">
                        {[labels.banquet, labels.coctel, labels.escuela, labels.auditorio, labels.u].map((l) => (
                          <th key={l} className="p-3 text-[10px] font-bold uppercase tracking-[0.16em] text-terra">{l}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {p.capacidades.map((c, i) => (
                          <td key={i} className="text-center !text-base font-serif !text-ink">{c}</td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-3 text-xs text-ink-mute">“-” = {t.lang === 'es' ? 'no aplica' : 'n/a'}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}